import { createStore, RouterState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { BUCKETS, IS_DEVELOPMENT } from '~/modules/config';
import { getPreferredLanguage } from '~/modules/helpers/languageHelper';
import { EXPLORE_BASE_ROUTE } from '~/modules/routes';
import { ExploreResultListing, getBucketSections } from '~/modules/services/exploreService';
import { i18nStore } from '~/modules/stores/i18nStore';
import { routerStore } from '~/modules/stores/routerStore';
import { JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { logger } from '../utilities/logger';

export interface BucketSection {
    name: string;
    label: string;
    url: string;
    listings: ExploreResultListing[];
}

export const RESULT_STATUS = {
    LOADING: 'LOADING',
    ERROR: 'ERROR',
    EMPTY: 'EMPTY',
    DONE: 'DONE',
};

export type ResultStatus = (typeof RESULT_STATUS)[keyof typeof RESULT_STATUS];

type Buckets = (typeof BUCKETS)[keyof typeof BUCKETS];

export interface BucketResult {
    status: ResultStatus;
    sections: BucketSection[];
}

export type SectionLabels =
    | I18nMessages['oaf']['category']
    | I18nMessages['search']['filters']['product']
    | I18nMessages['search']['filters']['solutionType']
    | I18nMessages['search']['filters']['solutionType_section']
    | I18nMessages['search']['filters']['impact_section']
    | I18nMessages['oaf']['supportedIndustries'];

export interface ExploreState extends State {
    currentBucket: Buckets;
    bucketSections: Map<Buckets, BucketResult>;
}

const initialState: ExploreState = {
    currentBucket: '',
    bucketSections: new Map<Buckets, BucketResult>(),
};

export const getLoadingBucketResult = (): BucketResult => {
    return {
        status: RESULT_STATUS.LOADING,
        sections: [],
    };
};

export type CurrentBucketResults = { bucketResult: BucketResult | undefined; currentBucket: string };
export const selectBucketResults = (state: ExploreState): CurrentBucketResults => {
    return {
        bucketResult: state.bucketSections.get(state.currentBucket),
        currentBucket: state.currentBucket,
    };
};

export const selectCurrentBucket = (state: ExploreState): string => {
    return state.currentBucket;
};

export const getSectionLabel = (bucket: string) => {
    switch (bucket) {
        case BUCKETS.EXPLORE_BUSINESS_NEEDS:
            return i18nStore.getState().i18nStaticCache.oaf.category;
        case BUCKETS.EXPLORE_PRODUCTS:
            return i18nStore.getState().i18nStaticCache.search.filters.product;
        case BUCKETS.EXPLORE_INDUSTRIES:
            return i18nStore.getState().i18nStaticCache.oaf.supportedIndustries;
        case BUCKETS.EXPLORE_IMPACT:
            return i18nStore.getState().i18nStaticCache.search.filters.impact_section;
        case BUCKETS.EXPLORE_AGENTFORCE:
            return i18nStore.getState().i18nStaticCache.oaf.category;
        case BUCKETS.EXPLORE_LABS:
            if (getPreferredLanguage() === JAPANESE_LANGUAGE_CODE) {
                return i18nStore.getState().i18nStaticCache.search.filters.solutionType;
            }
            return i18nStore.getState().i18nStaticCache.search.filters.solutionType_section;
        default:
            return undefined;
    }
};

export const exploreStore = createStore(
    initialState,

    (set: SetState<ExploreState>, get: GetState<ExploreState>) => ({
        setError(bucket: Buckets, error: Error) {
            set(function setError(draft: ExploreState) {
                draft.bucketSections.set(bucket, {
                    status: RESULT_STATUS.ERROR,
                    sections: [],
                });
                return draft;
            });
            logger.error(`Error fetching bucket sections for ${bucket}`, error);
        },
        setLoading(bucket: Buckets) {
            set(function setLoading(draft: ExploreState) {
                draft.bucketSections.set(bucket, getLoadingBucketResult());
                return draft;
            });
        },
        setCurrentBucket(bucket: Buckets) {
            set(function setBucket(draft: ExploreState) {
                draft.currentBucket = bucket;
                return draft;
            });
        },
        async fetchBucketSections(bucket: Buckets, forceFetch = false) {
            const { currentBucket, bucketSections } = get();

            const currentSection: BucketResult | undefined = bucketSections.get(bucket);

            if (!forceFetch) {
                if (currentBucket === bucket && currentSection?.status === RESULT_STATUS.DONE) {
                    return;
                }

                if (currentSection) {
                    set(function updateCategoryResults(draft: ExploreState) {
                        draft.currentBucket = bucket;
                        return draft;
                    });
                    return;
                }
            }

            this.setCurrentBucket(bucket);
            this.setLoading(bucket);

            try {
                const labels = getSectionLabel(bucket);
                if (labels) {
                    const sections = await getBucketSections(bucket, labels);
                    const bucketStatus: ResultStatus = sections.length === 0 ? RESULT_STATUS.EMPTY : RESULT_STATUS.DONE;
                    const bucketResult: BucketResult = {
                        status: bucketStatus,
                        sections: sections,
                    };
                    set(function updateCategoryResults(draft: ExploreState) {
                        draft.bucketSections.set(bucket, bucketResult);
                    });
                }
            } catch (error) {
                this.setError(bucket, error);
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'exploreStore',
    IS_DEVELOPMENT,
);

routerStore.subscribe(
    ({ data, url, params }) => {
        if (url.indexOf(EXPLORE_BASE_ROUTE) === -1) {
            exploreStore.reset();
            return;
        }

        if (!data || !data.bucket) {
            return;
        }

        if (!params) {
            exploreStore.fetchBucketSections(data.bucket as Buckets);
        } else {
            exploreStore.setCurrentBucket(data.bucket as Buckets);
        }
    },
    (state: RouterState) => ({ data: state.data, url: state.url, params: state.params }),
);
